// Moves the player (and their current army) between connected locations.

import type { LocationId, LoopState, PermanentState, Route } from "../types"
import { getFactionById, getLocationById, getRouteById } from "./content"
import { advanceDays } from "./timeEngine"
import { resolveEventsForRange } from "./eventEngine"
import { isTrustAtLeast } from "./factionEngine"
import { getAbilityLevel } from "./abilityEngine"

/**
 * What a leg of road actually costs a rider. Riding — the formerly inert
 * heartland ability — shaves days off travel, the game's only real currency, so
 * a trained horseman covers the same map in fewer days both in the calendar and
 * in every quoted estimate. A single day of road is already the floor (you
 * cannot arrive before you set out), so only multi-day legs shorten, and never
 * below one day. Every UI that quotes a travel cost, and the actual spend in
 * travel(), routes through here so the button never promises a day it will not
 * take.
 */
export function effectiveTravelDays(baseDays: number, permanent: PermanentState): number {
  if (baseDays <= 1) return baseDays
  const level = getAbilityLevel(permanent, "riding")
  const reduction =
    level === "master"
      ? 2
      : level === "advanced"
        ? 1
        : level === "beginner"
          ? // A novice's saving only shows on the longer hauls.
            baseDays >= 3
            ? 1
            : 0
          : 0
  return Math.max(1, baseDays - reduction)
}

/**
 * Whether a route is physically passable — distinct from whether a border
 * beyond it will admit the player (that is the trust gate, checked separately
 * per destination). An unlocked route is always open; a locked one opens once
 * the player has learned any of the knowledge that reveals it (Phase 7's
 * unlock methods, all expressed as permanent knowledge). Openness depends on
 * permanent state alone, so map discovery — which only has permanent state to
 * hand — can consult it too.
 */
export function isRouteOpen(route: Route, permanent: PermanentState): boolean {
  if (!route.locked) return true
  const unlock = route.unlock
  if (!unlock) return false
  return unlock.knowledgeAny.some((id) => permanent.knowledge.includes(id))
}

/**
 * Some borders are closed to strangers: Phase 4 lists "access to locations"
 * among the things a faction's trust can grant. Returns null when the player
 * may enter, or an explanation of the trust they still need.
 */
export function getTravelBlockReason(
  loop: LoopState,
  destinationId: LocationId,
): string | null {
  const gate = getLocationById(destinationId)?.requiresTrust
  if (!gate) return null
  if (isTrustAtLeast(loop, gate.factionId, gate.level)) return null
  const factionName = getFactionById(gate.factionId)?.name ?? gate.factionId
  return `The ${factionName} will not admit a stranger — you must first be ${gate.level} by them.`
}

export function travel(
  loop: LoopState,
  routeId: string,
  permanent: PermanentState,
): LoopState {
  const route = getRouteById(routeId)
  if (!route) {
    throw new Error(`Unknown route: ${routeId}`)
  }
  if (!isRouteOpen(route, permanent)) {
    throw new Error(`Route is locked: ${routeId}`)
  }
  if (
    route.fromLocationId !== loop.currentLocationId &&
    route.toLocationId !== loop.currentLocationId
  ) {
    throw new Error(`Route ${routeId} does not connect to the current location`)
  }

  const destinationId =
    route.fromLocationId === loop.currentLocationId
      ? route.toLocationId
      : route.fromLocationId

  const blockReason = getTravelBlockReason(loop, destinationId)
  if (blockReason) {
    throw new Error(blockReason)
  }

  const dayBeforeTravel = loop.currentDay
  const traveled = advanceDays(loop, effectiveTravelDays(route.travelDays, permanent))
  const withEvents = resolveEventsForRange(
    traveled,
    dayBeforeTravel + 1,
    traveled.currentDay,
  )

  return { ...withEvents, currentLocationId: destinationId }
}
